import { z } from 'zod';

import { PageStatusFilterValueKey } from '../filters/page-status-filter';
import { TypeFilterValueKey } from '../filters/universal-type';
import {
	type ProductKeys as ProductKeysFromProducts,
	ProductKeys as ProductKeysMap,
} from '../products';

import { isForgeConnectorProductKey } from './3p-config';

// Do not use z.coerce.boolean because JS type coercion from string to boolean is not logical
export const booleanSchema = z.enum(['true', 'false']).transform((x) => x === 'true');
export const commaSeparatedArraySchema = z.string().transform((x) => x.split(','));

// Schema that accepts both enum values and dynamic Forge connectors
export const productKeySchema = z.union([
	z.nativeEnum(ProductKeysMap),
	z.string().refine(isForgeConnectorProductKey, (val) => ({
		message: `${val} is not a valid product key`,
	})),
]);
// Use the same ProductKeys type as products.tsx to ensure type compatibility
// This avoids needing casts everywhere - both types are now identical
export type ProductKeys = ProductKeysFromProducts;
export const ProductKey = z.nativeEnum(ProductKeysMap).enum;

export const allContentTypeSchema = z.nativeEnum({
	All: 'all',
});
export type AllContentTypes = z.infer<typeof allContentTypeSchema>;
export const AllContentType = allContentTypeSchema.enum;

export const atlasContentTypeSchema = z.nativeEnum({
	Goal: 'goal',
	Learning: 'learning',
	Project: 'project',
	Tag: 'tag',
} as const);
export type AtlasContentTypes = z.infer<typeof atlasContentTypeSchema>;
export const AtlasContentType = atlasContentTypeSchema.enum;

export const asanaContentTypeSchema = z.nativeEnum({
	WorkItem: 'work-item',
} as const);

export type AsanaContentType = z.infer<typeof asanaContentTypeSchema>;
export const AsanaContentType = asanaContentTypeSchema.enum;

export const confluenceContentTypeSchema = z.nativeEnum({
	Answer: 'answer',
	Attachment: 'attachment',
	Blogpost: 'blogpost',
	Calendar: 'calendar',
	Comment: 'comment',
	Database: 'database',
	Document: 'document',
	Embed: 'embed',
	Folder: 'folder',
	Gliffy: 'gliffy',
	Page: 'page',
	Question: 'question',
	Space: 'space',
	SpaceCalendar: 'space-calendar',
	Whiteboard: 'whiteboard',
} as const);
export type ConfluenceContentTypes = z.infer<typeof confluenceContentTypeSchema>;
export const ConfluenceContentType = confluenceContentTypeSchema.enum;

export const googleContentTypeSchema = z.nativeEnum({
	Document: 'document',
	Presentation: 'presentation',
	Spreadsheet: 'spreadsheet',
	MeetingRecording: 'meeting-recording',
} as const);
export type GoogleContentTypes = z.infer<typeof googleContentTypeSchema>;
export const GoogleContentType = googleContentTypeSchema.enum;

export const microsoftContentTypeSchema = z.nativeEnum({
	Document: 'document',
	Presentation: 'presentation',
	Spreadsheet: 'spreadsheet',
} as const);
export type MicrosoftContentTypes = z.infer<typeof microsoftContentTypeSchema>;
export const MicrosoftContentType = microsoftContentTypeSchema.enum;

export const dropboxContentTypeSchema = z.nativeEnum({
	Document: 'document',
	Presentation: 'presentation',
	Spreadsheet: 'spreadsheet',
} as const);
export type DropboxContentTypes = z.infer<typeof dropboxContentTypeSchema>;
export const DropboxContentType = dropboxContentTypeSchema.enum;

export const jiraContentTypeSchema = z.nativeEnum({
	Project: 'project',
	Incident: 'incident',
	Issue: 'issue',
	Filter: 'filter',
	Dashboard: 'dashboard',
	Board: 'board',
	Plan: 'plan',
	Epic: 'epic',
	Bug: 'bug',
	Story: 'story',
	Task: 'task',
	Subtask: 'subtask',
} as const);
export type JiraContentTypes = z.infer<typeof jiraContentTypeSchema>;
export const JiraContentType = jiraContentTypeSchema.enum;

export const compassContentTypeSchema = z.nativeEnum({
	Component: 'component',
} as const);
export type CompassContentTypes = z.infer<typeof compassContentTypeSchema>;
export const CompassContentType = compassContentTypeSchema.enum;

export const mercuryContentTypeSchema = z.nativeEnum({
	FocusArea: 'focus-area',
} as const);
export type MercuryContentTypes = z.infer<typeof mercuryContentTypeSchema>;
export const MercuryContentType = mercuryContentTypeSchema.enum;

export const talentContentTypeSchema = z.nativeEnum({
	Position: 'position',
} as const);
export type TalentContentTypes = z.infer<typeof talentContentTypeSchema>;
export const TalentContentType = talentContentTypeSchema.enum;

export const trelloContentTypeSchema = z.nativeEnum({
	Board: 'board',
	Card: 'card',
} as const);
export type TrelloContentTypes = z.infer<typeof trelloContentTypeSchema>;
export const TrelloContentType = trelloContentTypeSchema.enum;

export const slackContentTypeSchema = z.nativeEnum({
	Message: 'message',
	Conversation: 'conversation',
} as const);
export type SlackContentTypes = z.infer<typeof slackContentTypeSchema>;
export const SlackContentType = slackContentTypeSchema.enum;

export const teamsContentTypeSchema = z.nativeEnum({
	Message: 'message',
	Conversation: 'conversation',
	MeetingTranscripts: 'meeting-transcripts',
	Attachment: 'attachment',
} as const);
export type TeamsContentTypes = z.infer<typeof teamsContentTypeSchema>;
export const TeamsContentType = teamsContentTypeSchema.enum;
export const githubContentTypeSchema = z.nativeEnum({
	Repository: 'repository',
	PullRequest: 'pull-request',
	Commit: 'commit',
	Branch: 'branch',
} as const);

export type GithubContentType = z.infer<typeof githubContentTypeSchema>;
export const GithubContentType = githubContentTypeSchema.enum;

export const webPageContentTypeSchema = z.nativeEnum({
	WebPage: 'web-page',
} as const);
export type WebPageContentTypes = z.infer<typeof webPageContentTypeSchema>;
export const WebPageContentType = webPageContentTypeSchema.enum;

export const figmaContentTypeSchema = z.nativeEnum({
	Design: 'design',
} as const);
export type FigmaContentTypes = z.infer<typeof figmaContentTypeSchema>;
export const FigmaContentType = figmaContentTypeSchema.enum;

export const loomContentTypeSchema = z.nativeEnum({
	Video: 'video',
} as const);
export type LoomContentTypes = z.infer<typeof loomContentTypeSchema>;
export const LoomContentType = loomContentTypeSchema.enum;

export const zendeskContentTypeSchema = z.nativeEnum({
	WorkItem: 'work-item',
	Document: 'document',
} as const);
export type ZendeskContentType = z.infer<typeof zendeskContentTypeSchema>;
export const ZendeskContentType = zendeskContentTypeSchema.enum;

export const smartLinksContentTypeSchema = z.nativeEnum({
	Dashboard: 'dashboard',
	Invoice: 'invoice',
	Deal: 'deal',
} as const);
export type SmartLinksContentType = z.infer<typeof smartLinksContentTypeSchema>;
export const SmartLinksContentType = smartLinksContentTypeSchema.enum;

export const salesforceContentTypeSchema = z.nativeEnum({
	Deal: 'deal',
	CustomerOrg: 'customer-org',
} as const);
export type SalesforceContentTypes = z.infer<typeof salesforceContentTypeSchema>;
export const SalesforceContentType = salesforceContentTypeSchema.enum;

export const databricksContentTypeSchema = z.nativeEnum({
	Dashboard: 'dashboard',
	DataTable: 'data-table',
} as const);
export type DatabricksContentTypes = z.infer<typeof databricksContentTypeSchema>;
export const DatabricksContentType = databricksContentTypeSchema.enum;

export const hubspotContentTypeSchema = z.nativeEnum({
	Deal: 'deal',
	CustomerOrg: 'customer-org',
} as const);
export type HubspotContentTypes = z.infer<typeof hubspotContentTypeSchema>;
export const HubspotContentType = hubspotContentTypeSchema.enum;

export const assetsContentTypeSchema = z.nativeEnum({
	AssetsObject: 'object',
	Schema: 'schema',
	Type: 'type',
} as const);
export const AssetsContentType = assetsContentTypeSchema.enum;
export type AssetsContentTypes = z.infer<typeof assetsContentTypeSchema>;

// this is the original enum (prior to using TypeFilterValueKey), it is only used for testing purposes for now
export const contentTypeSchemaOriginal = z.nativeEnum({
	...AllContentType,
	...AsanaContentType,
	...AtlasContentType,
	...ConfluenceContentType,
	...GoogleContentType,
	...MicrosoftContentType,
	...JiraContentType,
	...CompassContentType,
	...MercuryContentType,
	...TrelloContentType,
	...SlackContentType,
	...TeamsContentType,
	...FigmaContentType,
	...GithubContentType,
	...LoomContentType,
	...WebPageContentType,
	...DropboxContentType,
	...ZendeskContentType,
	...SmartLinksContentType,
	...SalesforceContentType,
	...HubspotContentType,
	...DatabricksContentType,
	...AssetsContentType,
	...TalentContentType,
} as const);

export const contentTypeSchema = z.nativeEnum({
	...TypeFilterValueKey,
	...AllContentType,
} as const);

export type ContentTypes = z.infer<typeof contentTypeSchema>;
export const ContentType = contentTypeSchema.enum;

export const pageStatusSchema = z.nativeEnum({
	...PageStatusFilterValueKey,
	...AllContentType,
});

export const lastModifiedValueSchema = z.nativeEnum({
	Anytime: 'anytime',
	Today: 'today',
	Yesterday: 'yesterday',
	Past7Days: 'past7Days',
	Past30Days: 'past30Days',
	PastYear: 'pastYear',
	Custom: 'custom',
} as const);
export const LastModified = lastModifiedValueSchema.enum;
export type LastModifiedValue = z.infer<typeof lastModifiedValueSchema>;

export const searchQueryParamsBaseSchema = z.object({
	text: z.optional(z.string().max(200)), // TODO: >>>> Ask how long should be the max query string?
	originalQuery: z.optional(z.string().max(200)), // Used to support "Did you mean" feature
	product: z.optional(z.string().pipe(productKeySchema)),
	datasource: z.optional(z.string()),
	type: z.optional(z.string().pipe(contentTypeSchema)),
	filterDropdownOpen: z.optional(booleanSchema),

	// Last Modified
	lastModified: z.optional(z.string().pipe(lastModifiedValueSchema)),
	lastModified_lt: z.optional(z.string().pipe(z.coerce.date())),
	lastModified_gt: z.optional(z.string().pipe(z.coerce.date())),
	from: z.optional(z.string()),
	to: z.optional(z.string()),

	titleMatch: z.optional(booleanSchema),

	// Confluence filters
	contributors: z.optional(commaSeparatedArraySchema),
	owners: z.optional(commaSeparatedArraySchema),
	ancestor: z.optional(z.string()),
	spaces: z.optional(commaSeparatedArraySchema),
	verified: z.optional(booleanSchema),
	labels: z.optional(commaSeparatedArraySchema),
	pageStatus: z.optional(z.string().pipe(pageStatusSchema)),
	archived: z.optional(booleanSchema),

	// Jira Filters
	jAssignee: z.optional(commaSeparatedArraySchema),
	jReporter: z.optional(commaSeparatedArraySchema),
	jCommentedByMe: z.optional(booleanSchema),
	jProject: z.optional(commaSeparatedArraySchema),
	jWatchedByMe: z.optional(booleanSchema),

	// Assets Filters
	assetsSchema: z.optional(commaSeparatedArraySchema),
	assetsType: z.optional(commaSeparatedArraySchema),

	// Mercury Filters
	focusAreaTypes: z.optional(commaSeparatedArraySchema),
	focusAreaUnder: z.optional(commaSeparatedArraySchema),

	// Talent Filters
	positionEmploymentTypes: z.optional(commaSeparatedArraySchema),
	positionFocusAreas: z.optional(commaSeparatedArraySchema),
	positionJobFamilies: z.optional(commaSeparatedArraySchema),
	positionStatuses: z.optional(commaSeparatedArraySchema),
	positionTeams: z.optional(commaSeparatedArraySchema),

	// Compass Filters
	compassArchived: z.optional(booleanSchema),

	createdBy: z.optional(commaSeparatedArraySchema),

	lastUpdatedBy: z.optional(commaSeparatedArraySchema),

	referrer: z.optional(z.string()),

	cql: z.optional(z.string()),

	status: z.optional(commaSeparatedArraySchema),
});

export type SearchQueryStringParams = z.infer<typeof searchQueryParamsBaseSchema>;
export const SearchQueryStringParamKey = searchQueryParamsBaseSchema.keyof().enum;
export type SearchQueryStringParamKeys = keyof SearchQueryStringParams;
export type SearchQueryStringParamsRaw = z.input<typeof searchQueryParamsBaseSchema>;
type ExtractKeysWithArrayValues<T> = {
	[P in keyof T]: T[P] extends unknown[] ? P : never;
}[keyof T];
export type SearchQueryStringParamKeysWithValues = NonNullable<
	ExtractKeysWithArrayValues<SearchQueryStringParams>
>;

export const searchQueryParamsValidators = [
	(queryParams) => {
		if (queryParams.lastModified !== 'custom') {
			const { lastModified_gt, lastModified_lt, ...restQueryParams } = queryParams;
			return restQueryParams;
		}
		return queryParams;
	},
	(queryParams) => {
		const { lastModified_lt, lastModified_gt } = queryParams;
		if (lastModified_lt && lastModified_gt && lastModified_lt > lastModified_gt) {
			const { lastModified, lastModified_gt, lastModified_lt, ...restQueryParams } = queryParams;
			return restQueryParams;
		}
		return queryParams;
	},
] satisfies ((queryParams: SearchQueryStringParams) => SearchQueryStringParams)[];

export const filterKeysSchema = z.nativeEnum({
	...ProductKey,
	...ContentType,
	...LastModified,
	...SearchQueryStringParamKey,
} as const);
export const FilterKey = filterKeysSchema.enum;
export type FilterKeys = z.infer<typeof filterKeysSchema>;
export type FilterKeysRaw = boolean | readonly string[];
